import React, { useState, useEffect } from 'react';
import { Input } from '../ui/input';
import { MapPin } from 'lucide-react';
import { noAutofillProps } from '../../utils/noAutofill';

const AddressAutocomplete = ({
  placeholder,
  value,
  onValueChange,
  onPlaceSelect,
  types = [],
  className
}) => {
  const [query, setQuery] = useState(value || '');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);

  // Mock airport data for demonstration - in production this would use Google Places API
  const mockAirports = [
    {
      name: 'Los Angeles International Airport',
      formatted_address: 'Los Angeles, CA, USA',
      place_id: 'lax-airport',
      types: ['airport']
    },
    {
      name: 'John F. Kennedy International Airport',
      formatted_address: 'New York, NY, USA', 
      place_id: 'jfk-airport',
      types: ['airport']
    },
    {
      name: 'Chicago O\'Hare International Airport',
      formatted_address: 'Chicago, IL, USA',
      place_id: 'ord-airport', 
      types: ['airport']
    },
    {
      name: 'Miami International Airport',
      formatted_address: 'Miami, FL, USA',
      place_id: 'mia-airport',
      types: ['airport']
    },
    {
      name: 'Denver International Airport',
      formatted_address: 'Denver, CO, USA',
      place_id: 'den-airport',
      types: ['airport']
    },
    {
      name: 'San Francisco International Airport',
      formatted_address: 'San Francisco, CA, USA',
      place_id: 'sfo-airport',
      types: ['airport']
    }
  ];

  // Initialize query from value prop only once
  useEffect(() => {
    if (value && value !== query) {
      setQuery(value);
    }
  }, []); // Only run once on mount

  useEffect(() => {
    if (query.length > 1) {
      setLoading(true);
      // Simulate API call delay
      const timer = setTimeout(() => {
        let filtered = mockAirports.filter(airport =>
          airport.name.toLowerCase().includes(query.toLowerCase()) ||
          airport.formatted_address.toLowerCase().includes(query.toLowerCase())
        );
        
        // Filter by types if specified
        if (types.length > 0) {
          filtered = filtered.filter(airport =>
            types.some(type => airport.types.includes(type))
          );
        }
        
        setSuggestions(filtered);
        setShowSuggestions(true);
        setLoading(false);
      }, 300);
      
      return () => clearTimeout(timer);
    } else {
      setShowSuggestions(false);
      setSuggestions([]);
    }
  }, [query, types]);

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setQuery(newValue);
    
    // Call onValueChange only when user types, not during initialization
    if (onValueChange) {
      onValueChange(newValue);
    }
  };

  const handlePlaceSelect = (place) => {
    setQuery(place.name);
    setShowSuggestions(false);
    if (onPlaceSelect) {
      onPlaceSelect(place);
    }
  };

  return (
    <div className="relative">
      <Input
        {...noAutofillProps}
        name="address-search"
        value={query}
        onChange={handleInputChange}
        onFocus={() => {
          if (query.length > 1) {
            setShowSuggestions(true);
          }
        }}
        onBlur={() => {
          // Delay hiding suggestions to allow clicks
          setTimeout(() => setShowSuggestions(false), 200);
        }}
        placeholder={placeholder}
        className={className}
      />
      
      {showSuggestions && (
        <div className="absolute z-10 w-full mt-1 bg-ui-surface border border-ui-line rounded-lg shadow-lg max-h-48 overflow-y-auto">
          {loading ? (
            <div className="p-3 text-center text-ui-muted">
              Searching locations...
            </div>
          ) : suggestions.length > 0 ? (
            suggestions.map((place) => (
              <div
                key={place.place_id}
                className="p-3 hover:bg-ui-hover cursor-pointer border-b border-ui-divider last:border-b-0"
                onClick={() => handlePlaceSelect(place)}
              >
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-ui-blue-bg rounded-lg flex items-center justify-center">
                    <MapPin className="w-4 h-4 text-ui-link" />
                  </div>
                  <div className="flex-1">
                    <div className="font-medium text-ui-ink">{place.name}</div>
                    <div className="text-sm text-ui-muted">{place.formatted_address}</div>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="p-3 text-center text-ui-muted">
              No locations found
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AddressAutocomplete;